import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import moment from "moment";
import BookingDatePicker from "../common/components/OrderDataStep/BookingDatePicker";
import {
  checkInDateSelected,
  checkOutDateSelected,
  checkOutDateSelectedClear,
  startTimeSelected,
  endTimeSelected,
  eventTypeSelected,
  setNextEnabled,
} from "../../../redux/slice/bookingCreation/bookingCreationSlice";
import NumericInput from "../../common/NumberOfCustomers";
import CityAndLocationSelection from "./components/CityAndDistrictSelection";
import {useGetMaxGuestsQuery} from "../../../api/locations/locationsApiSlice";
import React, {SyntheticEvent, useMemo, useState} from "react";
import AutoCompleteSelect from "../../layout/components/AutoCompleteSelect";
import {i18n} from "../../../i18n/i18n";
import BookingTimeSelect from "../common/components/OrderDataStep/BookingTimeSelect";
import {eventTypes, getAvailableBookingTimes} from "../../../util";

const LocationAndDurationSelectionStep = () => {
  const { t } = useTranslation();
  const locations = useSelector(
      (state: RootState) => state.booking.useCaseLocations
  );

  const { data: guestsMax } = useGetMaxGuestsQuery(undefined, {
    refetchOnMountOrArgChange: 60
  });

  const eventType = useSelector((state: RootState) => state.bookingCreation.orderData.eventType);
  const startTime = useSelector((state: RootState) => state.bookingCreation.orderData.startTime || "0");
  const endTime = useSelector((state: RootState) => state.bookingCreation.orderData.endTime || "0");

  const startDate = useSelector(
      (state: RootState) => state.bookingCreation.orderData.checkInDate
  );
  const endDate = useSelector(
      (state: RootState) => state.bookingCreation.orderData.checkOutDate
  );

  const numberOfDays = useSelector((state: RootState) => state.bookingCreation.orderData.numberOfDays || 0);

  const dispatch = useDispatch();

  const handleDateChange = (
      start: moment.Moment | null,
      end: moment.Moment | null
  ) => {
    if (start) {
      dispatch(checkInDateSelected({ checkInDate: start.startOf("day") }));
      dispatch(checkOutDateSelectedClear());
    }
    if (end) {
      if (start && end.isBefore(start)) {
        // Ensure checkout date is not before check-in date
        dispatch(checkOutDateSelected({ checkOutDate: start.clone() }));
      } else {
        dispatch(checkOutDateSelected({ checkOutDate: end }));
      }
    }
  };

  const generateAvailableDates = (minDate?: moment.Moment | null) => {
    const fiveDaysLater = new Date();
    fiveDaysLater.setDate(fiveDaysLater.getDate() + 5);

    const start = minDate || moment(fiveDaysLater).startOf("day");
    const end = moment().add(18, "months").endOf("month").startOf("day"); // End of the month, 18 months later
    const dates: string[] = [];

    for (let date = start.clone(); date.isBefore(end); date.add(1, "day")) {
        dates.push(date.format("DD-MM-YYYY"));
    }

    return dates;
  };

  const generateAvailableCheckOutDates = (checkInDate: moment.Moment | null) => {
    if (!checkInDate) return generateAvailableDates();

    const start = checkInDate.clone(); // allow same day
    const end = moment().add(18, "months").endOf("month").startOf("day"); // End of the month, 1 year later
    const dates: any = [];

    for (let date = start; date.isBefore(end); date.add(1, "day")) {
      dates.push(date.format("DD-MM-YYYY"));
    }

    return dates;
  };

  const availableCheckInDates = generateAvailableDates();
  const availableCheckOutDates = generateAvailableCheckOutDates(startDate);

  const roomRentalLocation = useSelector(
      (state: RootState) => state.bookingCreation.roomRentalLocation
  )

  const currentGuestsMax: number = useMemo(() => {
    if (guestsMax && roomRentalLocation?.city) {
      return guestsMax[roomRentalLocation.city] || 100;
    }
    return 100
  }, [roomRentalLocation, guestsMax])



  const handleEventTypeChange = (
      _: SyntheticEvent<Element, Event>,
      value: string | null
  ) => {
    dispatch(eventTypeSelected({ eventType: value }));
    dispatch(setNextEnabled(!!value));
  }

  // Generate available time slots for start and end time
  let availableStartTimes: string[] = [];
  let availableEndTimes: string[] = [];
  if (startDate && endDate) {
    try {
      const { availableStartTimes: startTimes, availableEndTimes: endTimes } = getAvailableBookingTimes(
        startDate.toISOString(),
        endDate.toISOString(),
        startTime,
        endTime,
      );
      availableStartTimes = startTimes;
      availableEndTimes = endTimes;
    } catch (e) {
      // Invalid date range, keep arrays empty
    }
  }

  const handleStartTimeChange = (value: string) => {
    dispatch(startTimeSelected({ startTime: value }));
  };

  const handleEndTimeChange = (value: string) => {
    dispatch(endTimeSelected({ endTime: value }));
  };

  console.log('availableStartTimes', availableStartTimes);
  console.log('availableEndTimes', availableEndTimes);

  return (
      <div className="flex flex-col w-full max-w-2xl gap-6 px-3 mx-auto sm:px-5">
        <div className="w-full">
          {locations ? (
              <CityAndLocationSelection locations={locations} />
          ) : (
              <p className="text-gray-700">{t("book_location.no_location")}</p>
          )}
        </div>

        <div className="w-full flex items-center justify-between">
          <div className="">
            <BookingDatePicker
                value={startDate || null}
                onChange={(date) => handleDateChange(date || null, endDate)}
                shouldDisableDate={(date) => false}
                availableDates={availableCheckInDates}
                label={t("apartment.check_in")}
            />
          </div>
          <div className="flex items-center justify-center text-gray-400 font-bold mx-4">
            -
          </div>
          <div className="">
            <BookingDatePicker
                value={endDate || null}
                onChange={(date) => handleDateChange(startDate, date || null)}
                shouldDisableDate={(date) => {
                  if (startDate) {
                    return date ? date.isBefore(startDate) : false;
                  }
                  return false;
                }}
                availableDates={availableCheckOutDates}
                label={t("apartment.check_out")}
            />
          </div>
        </div>

        <div className="w-full flex items-center justify-between">
          <div className="w-full">
            <BookingTimeSelect
                availableSlots={availableStartTimes}
                value={startTime}
                onChange={handleStartTimeChange}
                availableDurations={[]}
                // disabled={!startDate || !endDate}
                formControlClassName={"w-full"}
                label={t("general.start_time")}
            />
          </div>
          <div className="flex items-center justify-center text-gray-400 font-bold mx-4">
            -
          </div>
          <div className="w-full">
            <BookingTimeSelect
                availableSlots={availableEndTimes}
                value={endTime}
                onChange={handleEndTimeChange}
                availableDurations={[]}
                // disabled={!startDate || !endDate}
                formControlClassName={"w-full"}
                label={t("general.end_time")}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-x-2 p-2 sm:flex-row sm:items-center sm:px-3 sm:gap-x-4">
          <span className="text-base font-normal text-gray-600 sm:text-base">
            {t("apartment.number_guests")}
          </span>
            <NumericInput max={currentGuestsMax} />
        </div>
        <div className={"mt-1 mb-6"}>
          <AutoCompleteSelect
              options={eventTypes[i18n.language as "en" | "de"] ?? eventTypes["en"]}
              value={eventType ?? null}
              textLabel={t("venue_reservation.event_type")}
              handleSelection={handleEventTypeChange}
              allowTyping={false}
              disabled={false}
          />
        </div>
      </div>
  );
};

export default LocationAndDurationSelectionStep;
